import debug from "debug";
import Experience from "../Experience";

const log = debug("app:ur");

/**
 * The `MakePartsPreprocessor` class is responsible for processing input data related to part creation, 
 * adjusting coordinates, and handling object rotations.
 * @category Experience
 * @subcategory World
 */
export default class MakePartsPreprocessor {
  /**
   * Constructs an instance of `MakePartsPreprocessor` and initializes dependencies.
   */
  constructor() {
    this.experience = new Experience();
    this.eventHub = this.experience.eventHub;
  }

  cleanup() {
    try {
      log("Cleaning up MakePartsPreprocessor...");
  
      if (this.eventHub) {
        this.eventHub.off("makeParts", this.processInput);
        this.eventHub.off("makePartsLoader", this.processInput);
      }
  
      // Clear references to avoid memory leaks
      this.experience = null;
      this.eventHub = null;
  
      log("MakePartsPreprocessor cleanup completed.");
    } catch (error) {
      console.error("Error during MakePartsPreprocessor cleanup:", error);
    }
  }

  /**
   * Initializes the preprocessor based on the provided configuration.
   * @param {object} config - Configuration object containing the method type and arguments.
   * @returns {Promise<object|null>} - Processed data or null if an error occurs.
   */
  async init(config) {
    try {
      if (config.method === "process") {
        log("process");
        log("arg", config.arg);
        return await this.processInput(config.arg); // Return processed data
      } else if (config.method === "rotate") {
        log("rotate");
        return await this.rotateV1Objects(config.arg); // Return rotation result
      } else {
        throw new Error("Unknown method");
      }
    } catch (error) {
      console.error("Error in init method:", error);
      return null;
    }
  }

  /**
   * Processes input design data, adjusting coordinates based on version and part properties.
   * @param {object} designData - The design data to be processed.
   * @returns {object|null} - Adjusted design data or null if an error occurs.
   */
  async processInput(designData) {
    try {
      log("designData in processINput", designData);
      const adjustedDesignData = this.adjustAndRoundCoordinates(designData);
      return adjustedDesignData; // Return the processed designData
    } catch (error) {
      console.error("Error in processInput:", error);
      return null;
    }
  }

  /**
   * Adjusts and rounds coordinates of the design data.
   * @param {object} designData - The original design data.
   * @returns {object} - Adjusted design data with rounded coordinates.
   */
  adjustAndRoundCoordinates(designData) {
    log("Original designData", designData);

    // Check version and update mode within each figure's partData
    if (designData.version === "1.0") {
      designData.figures = (designData.figures || []).map((part) => {
        if (part.partData.mode === "user-cfg") {
          part.partData.mode = "cfg-part";
        }
        return part;
      });
    }

    // Determine factors and angle based on version
    const partFactor = designData.version === "1.0" ? 48 : 1;
    const textFactor = designData.version === "1.0" ? 48 : 1;
    const angleAdjustment = designData.version === "1.0" ? -1 : 1;

    log("Adjustments", partFactor, textFactor, angleAdjustment);
    log(designData);
    // Create a new object, copying designData and adjusting coordinates
    const adjustedDesignData = {
      ...designData,
      figures: (designData.figures || []).map((part) => ({
        ...part,
        x: Math.round(part.x / partFactor),
        z: Math.round(
          (designData.version === "1.0" ? part.y : part.z) / partFactor
        ),
        angle: Math.round(
          part.angle === 0 ? part.angle : part.angle * angleAdjustment
        ),
      })),
      others: (designData.others || []).map((textbox) => ({
        ...textbox,
        x: Math.round(textbox.x / textFactor),
        y: 1,
        z: Math.round(
          (designData.version === "1.0" ? textbox.y : textbox.z) / textFactor
        ),
        w: Math.round(textbox.w / textFactor),
        h: Math.round(textbox.h / textFactor),
      })),
    };

    log("Adjusted designData", adjustedDesignData);
    return adjustedDesignData; // Return the new object with adjusted values
  }

  /**
   * Rotates objects for version 1.0 using predefined rules based on part properties.
   * @param {object} params - Parameters including width, height, layer, and angle.
   * @returns {Promise<object|null>} - The computed rotation values or null if an error occurs.
   */
  async rotateV1Objects(params) {
    try {
      const { w, h, layer, angle } = params;
      log("rotate all params", w, h, layer, angle);

      const { x, z } = this.calculateRotation(w, h, layer, angle);

      return { x, z };
    } catch (error) {
      console.error("Error in rotateV1Objects:", error);
      return null;
    }
  }

  /**
   * Calculates rotation adjustments based on part properties and layer settings.
   * @param {number} w - Width of the part.
   * @param {number} h - Height of the part.
   * @param {number} layer - The layer on which the part resides.
   * @param {number} angle - The angle of rotation.
   * @returns {object} - Adjusted x and z coordinates after rotation.
   */
  calculateRotation(w, h, layer, angle) {
    log("rotate input data", w, h, layer, angle);
    let result;

    const angleV1 = -angle;
    if (layer === 50) {
      result = this.calculateRotationForLayer50(w, h, angleV1);
    } else if (layer === 40) {
      result = this.calculateRotationForLayer40(w, h, angleV1);
    } else {
      result = this.calculateRotationForDefaultLayer(w, h, angleV1);
    }
    log("rotationV1 result", result);
    return result;
  }

  /**
   * Calculates rotation adjustments for layer 40.
   * @param {number} w - Width of the part.
   * @param {number} h - Height of the part.
   * @param {number} angle - The angle of rotation.
   * @returns {object} - Adjusted x and z coordinates for layer 40.
   */
  calculateRotationForLayer40(w, h, angle) {
    let x, z;
    switch (angle) {
      case 0:
      case 180:
      case -180:
        x = w / 2;
        z = h / 2;
        break;
      case 90:
      case -270:
      case 270:
      case -90:
        x = h / 2;
        z = w / 2;
        break;
    }
    return { x, z };
  }

  /**
   * Calculates rotation adjustments for layer 50.
   * @param {number} w - Width of the part.
   * @param {number} h - Height of the part.
   * @param {number} angle - The angle of rotation.
   * @returns {object} - Adjusted x and z coordinates for layer 50.
   */
  calculateRotationForLayer50(w, h, angle) {
    let x, z;
    switch (angle) {
      case 0:
        x = w / 2;
        z = 0;
        break;
      case 90:
      case -270:
        x = h;
        z = w / 2;
        break;
      case 180:
      case -180:
        x = w / 2;
        z = h;
        break;
      case 270:
      case -90:
        x = 0;
        z = w / 2;
        break;
    }
    return { x, z };
  }

  /**
   * Calculates rotation adjustments for layers other than 40 and 50.
   * @param {number} w - Width of the part.
   * @param {number} h - Height of the part.
   * @param {number} angle - The angle of rotation.
   * @returns {object} - Adjusted x and z coordinates for default layers.
   */
  calculateRotationForDefaultLayer(w, h, angle) {
    let x, z;
    switch (angle) {
      case 0:
        x = 0;
        z = 0;
        break;
      case 90:
      case -270:
        x = h;
        z = 0;
        break;
      case 180:
      case -180:
        x = w;
        z = h;
        break;
      case 270:
      case -90:
        x = 0;
        z = w;
        break;
    }
    return { x, z };
  }
}
